import PropTypes from 'prop-types';

import Button from '@strava/ui/Button';

import React from 'react';
import I18n from 'utils/I18n';

import Modal from '@strava/ui/Modal';
import DropdownDatePicker from '../DropdownDatePicker';

import Helper from '../../../Show/components/ChallengeGalleryJoinButton/helper';
import styles from './styles.scss';
import GatingHelper from '../../helpers/gating_helper';

const I18nPromptHeading = 'strava.challenges.age_gate.prompt.heading';
const I18nPromptText = 'strava.challenges.age_gate.prompt.text_html';
const I18nPromptCTA = 'strava.challenges.age_gate.prompt.cta';
const I18nPromptDismiss = 'strava.challenges.age_gate.prompt.dismiss';
const I18nPromptFooter = 'strava.challenges.age_gate.prompt.footer';
const I18nDeniedHeading = 'strava.challenges.age_gate.denied.heading';
const I18nDeniedText = 'strava.challenges.age_gate.denied.text';
const I18nDeniedDismiss = 'strava.challenges.age_gate.denied.dismiss';

const YOUNGEST_AGE_ON_PLATFORM = 13;

const AgeGateModal = ({
  handleModalClose,
  handleDateSelectionChange,
  handleDateUpdateInProgress,
  handleDateUpdateSuccess,
  handleDateUpdateFailure,
  gatingModal,
  currentAthlete
}) => {
  if (!gatingModal || !currentAthlete) {
    return null;
  }

  const {
    gatingConditions,
    ageGate,
    joinChallengeFn,
    requestInProgress
  } = gatingModal;

  const { dateOfBirth } = currentAthlete;
  const { minimumAge } = gatingConditions;

  if (!minimumAge) {
    return null;
  }
  if (
    dateOfBirth &&
    GatingHelper.dateFromString(dateOfBirth) >
      GatingHelper.dateYearsAgo(minimumAge)
  ) {
    return (
      <Modal
        className={styles.modal}
        isOpen={true}
        onRequestClose={handleModalClose}
      >
        <div className="text-body text-center">
          <h2 className={`${styles.heading} text-title2`}>
            {I18n.t(I18nDeniedHeading, gatingConditions)}
          </h2>
          <p className={styles.deniedText}>
            {I18n.t(I18nDeniedText, gatingConditions)}
          </p>

          <div className={styles.controls}>
            <Button
              name="cancel"
              variant="primary"
              className={styles.btnModalDeniedClose}
              disabled={requestInProgress}
              onClick={handleModalClose}
            >
              {I18n.t(I18nDeniedDismiss)}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  const handleVerify = () => {
    const { dateSelected } = ageGate;
    const { id } = currentAthlete;

    const url = `/athletes/${id}`;
    const payload = {
      athlete: {
        'dateofbirth(1i)': dateSelected.getFullYear(),
        'dateofbirth(2i)': dateSelected.getMonth() + 1,
        'dateofbirth(3i)': dateSelected.getDate()
      }
    };

    handleDateUpdateInProgress();

    return Helper.instance()
      .put(url, payload, { headers: { Accept: 'text/javascript' } })
      .then(() => {
        handleDateUpdateSuccess(dateSelected);
        if (
          dateSelected > GatingHelper.dateYearsAgo(gatingConditions.minimumAge)
        ) {
          return;
        }
        joinChallengeFn();
      })
      .catch(() => handleDateUpdateFailure());
  };

  const dateSelected = ageGate && ageGate.dateSelected;
  const dateMax = GatingHelper.dateYearsAgo(YOUNGEST_AGE_ON_PLATFORM);

  return (
    <Modal
      className={styles.modal}
      isOpen={true}
      onRequestClose={handleModalClose}
    >
      <div className="text-body text-center">
        <h2 className="text-title2">
          {I18n.t(I18nPromptHeading, gatingConditions)}
        </h2>
        <p className={styles.promptText}>{I18n.t_html(I18nPromptText)}</p>

        <div className={styles.controls}>
          <DropdownDatePicker
            className={styles.datepicker}
            disabled={requestInProgress}
            {...{ dateMax, dateSelected }}
            dateChangeFn={handleDateSelectionChange}
          />
          <Button
            name="verify"
            variant="primary"
            className={styles.btnModalVerify}
            disabled={requestInProgress}
            onClick={handleVerify}
          >
            {I18n.t(I18nPromptCTA)}
          </Button>
          <Button
            name="cancel"
            className={styles.btnModalDismiss}
            disabled={requestInProgress}
            onClick={handleModalClose}
          >
            {I18n.t(I18nPromptDismiss)}
          </Button>
        </div>
        <small>{I18n.t(I18nPromptFooter, gatingConditions)}</small>
      </div>
    </Modal>
  );
};

AgeGateModal.defaultProps = {
  gatingModal: null,
  currentAthlete: null
};

AgeGateModal.propTypes = {
  handleModalClose: PropTypes.func.isRequired,
  handleDateSelectionChange: PropTypes.func.isRequired,
  handleDateUpdateInProgress: PropTypes.func.isRequired,
  handleDateUpdateSuccess: PropTypes.func.isRequired,
  handleDateUpdateFailure: PropTypes.func.isRequired,
  gatingModal: PropTypes.shape({
    joinChallengeFn: PropTypes.func.isRequired,
    gatingConditions: PropTypes.shape({
      minimumAge: PropTypes.number
    }).isRequired,
    ageGate: {
      dateSelected: PropTypes.instanceOf(Date)
    }.isRequired,
    requestInProgress: PropTypes.bool.isRequired
  }),
  currentAthlete: PropTypes.shape({
    id: PropTypes.number.isRequired,
    dateOfBirth: PropTypes.string
  })
};

export default AgeGateModal;
